import JSZip from 'jszip'
import { Result } from 'neverthrow'

import { TAttachmentWithURI } from 'src/parser/entities/Alarm/Preview/Provider/useAttachments'
import { TAdditionalDetails } from 'src/parser/entities/Alarm/Preview/Provider/useAdditionalDetails'
import { dataURIToBlob } from 'src/parser/entities/Alarm/UserMenu/dataURIToBlob'
import { getFileBlobsFromUrls } from 'src/parser/entities/Alarm/UserMenu/getFileBlobsFromUrls'
import { TExportFailure } from './exportErrors'

export type TArchiveEntry = {
  name: string
  content: Blob | string
}

export type TFetchBlobs = (
  urls: string[],
) => Promise<Record<string, Result<Blob, unknown>>>

export type TCollectInput = {
  alarmId: string
  prefix: string
  pdfBlob?: Blob
  attachments: TAttachmentWithURI[]
  additionalDetails: TAdditionalDetails
  fetchBlobs?: TFetchBlobs
}

export const cleanupFilename = (filename: string): string =>
  filename.replace(/[^a-z0-9.()-]/gi, '_')

export const makeUniqueNamer = (): ((suggestedName: string) => string) => {
  const usedNames = new Set<string>()

  return suggestedName => {
    let name = suggestedName
    let counter = 1
    const lastDot = name.lastIndexOf('.')
    const base = lastDot === -1 ? name : name.slice(0, lastDot)
    const ext = lastDot === -1 ? '' : name.slice(lastDot)

    while (usedNames.has(name)) {
      name = `${base}-${counter}${ext}`
      counter++
    }
    usedNames.add(name)
    return name
  }
}

const getResourceNameFromUrl = (url = ''): string =>
  cleanupFilename(url.substr(url.lastIndexOf('/') + 1))

const causeOf = (error: unknown): string =>
  error instanceof Error ? error.message : String(error)

export type TCollectResult = {
  entries: TArchiveEntry[]
  failures: TExportFailure[]
  addEntry: (name: string, content: Blob | string) => void
}

export const collectAlarmArchiveEntries = async ({
  alarmId,
  prefix,
  pdfBlob,
  attachments,
  additionalDetails,
  fetchBlobs = getFileBlobsFromUrls,
}: TCollectInput): Promise<TCollectResult> => {
  const entries: TArchiveEntry[] = []
  const failures: TExportFailure[] = []
  const unique = makeUniqueNamer()

  const push = (name: string, content: Blob | string): void => {
    entries.push({ name: `${prefix}${unique(name)}`, content })
  }

  const fail = (name: string, cause: unknown): void => {
    failures.push({
      code: 'MEDIA_FETCH_FAILED',
      alarmId,
      name,
      cause: causeOf(cause),
    })
  }

  if (pdfBlob !== undefined) push(`report-${alarmId}.pdf`, pdfBlob)

  const urlsFromAttachments: string[] = []

  attachments.forEach(attachment => {
    if (
      attachment.type.endsWith('Url') &&
      typeof attachment.data?.url === 'string'
    ) {
      urlsFromAttachments.push(attachment.data.url)
      return
    }

    if (attachment.file == null || typeof attachment.file.name !== 'string')
      return

    const name = cleanupFilename(attachment.file.name)

    if (attachment.dataURI == null) {
      fail(name, 'the attachment carries no data URI')
      return
    }

    attachment.dataURI.andThen(dataURIToBlob).match(
      blob => push(name, blob),
      error => fail(name, error),
    )
  })

  const {
    textDatas = [],
    imageUrls = [],
    videoUrls = [],
    audioUrls = [],
  } = additionalDetails

  const blobs = await fetchBlobs([
    ...videoUrls,
    ...audioUrls,
    ...imageUrls,
    ...urlsFromAttachments,
  ])

  Object.entries(blobs).forEach(([url, blob]) => {
    const name = getResourceNameFromUrl(url)
    blob.match(
      it => push(name, it),
      error => fail(name, error),
    )
  })

  textDatas.forEach((text, i) => {
    push(`AttachedText_${i}.txt`, text)
  })

  return { entries, failures, addEntry: push }
}

export const zipEntries = async (entries: TArchiveEntry[]): Promise<Blob> => {
  const zip = new JSZip()

  entries.forEach(({ name, content }) => {
    zip.file(name, content)
  })

  return await zip.generateAsync({ type: 'blob' })
}
